import { Trans } from 'react-i18next';
import { ReactNode, useMemo } from 'react';

import { loaders } from 'entities';
import { useRouteParams, useRouting } from 'routes';

import { Button, Column, Row } from 'components/Blocks';
import { ArrowLeftIcon, SearchIcon, WarningIcon } from 'components/Icons';

import { useLang } from 'hooks';

interface BadProjectIdProps {
  children: ReactNode;
}

function isValidProjectId(id: string) {
  const KSUID_LENGTH = 27;

  const isValid = id.length === KSUID_LENGTH && /^[0-9a-zA-Z]{27}$/.test(id);

  return isValid;
}

function isViableSampleId(id: string) {
  return /^\d{4,9}$/.test(id);
}

export function BadProjectIdGuard({ children }: BadProjectIdProps): JSX.Element {
  const { t } = useLang();

  const { projectProfile } = useRouting();
  const { projectId } = useRouteParams('projectProfile');

  const isValidId = useMemo(() => isValidProjectId(projectId), [projectId]);

  const { loading } = loaders.projects.findBySampleId.use({
    blockLoad: !isViableSampleId(projectId),

    params: { sampleId: projectId },

    onDataChange: ({ id }) => id && projectProfile({ projectId: id }),
  });

  if (isValidId) return <>{children}</>;

  return (
    <Column justifyContent="center" alignItems="center" fullHeight>
      <WarningIcon size={96} color="red" />

      <p className="text-2xl">{t('invalid-project-link')}</p>

      <p className="max-w-[560px] text-center">{t('invalid-link-details-1')}</p>

      <p className="max-w-[560px] text-center">
        <Trans>{t('invalid-link-details-2')}</Trans>
      </p>

      {loading && (
        <Row gap="2" className="font-sm text-warning">
          <SearchIcon size={12} />
          {t('searching-for-sample-id')}
        </Row>
      )}

      <Button variant="ghost" to="home">
        <ArrowLeftIcon />
        {t('back-home')}
      </Button>
    </Column>
  );
}
